import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { OUTCOME_FILTER_LABELS } from '../../types/predictedJeffBids';
import type { PredictionCategory, PredictionOutcomeFilter } from '../../types/predictedJeffBids';

/**
 * The narrowing controls inside the table frame (feature 025-ai-predicted-bids; categories and
 * sub-categories since 032-ai-prediction-categories).
 *
 * Thousands of predictions are far too many to walk end to end looking for anything in
 * particular, so the reviewer cuts the set by CATEGORY (one of six, always one — there is no
 * all-categories view, by user decision 2026-10-09), by SUB-CATEGORY within it ("All
 * sub-categories" or one), and by whether the AI matched Jeff, and jumps straight to a position
 * within whatever set that leaves.
 *
 * Changing the category resets the sub-category to "All sub-categories" of the new one: a
 * sub-category belongs to one category, so the old choice has no meaning under the new heading.
 *
 * They ride in the frame's `controls` slot — on the felt, directly under the masthead and above the
 * panels they narrow (client decision 2026-07-27) — so the labels are light-on-dark while the
 * fields themselves stay cream, matching every other input on the felt. The row owns no outer
 * width or horizontal padding: the frame supplies both. Each label sits ABOVE its control, so the
 * labels read as one row and the controls as a second (user, 2026-10-09): label and control side
 * by side made the four fields too wide for one line, and the last one wrapped under the first.
 *
 * The jump box is deliberately a position ("go to hand 240 of 705"), not a table-result id: the
 * progress counter beside it is a position, and offering two different numbering schemes for the
 * same set is how a reviewer ends up somewhere they did not intend.
 */

export interface PredictionFilterBarProps {
  /** The six categories in display order, each with its sub-categories in display order. */
  categories: PredictionCategory[];
  /** The selected category; null only while the index has not arrived. */
  categoryId: number | null;
  onCategoryChange: (categoryId: number) => void;
  /** The selected sub-category of the category, or null for all of them. */
  subcategoryId: number | null;
  onSubcategoryChange: (subcategoryId: number | null) => void;
  outcomeFilter: PredictionOutcomeFilter;
  onOutcomeChange: (outcome: PredictionOutcomeFilter) => void;
  /** How many hands the current filters leave — the jump box's upper bound. */
  filteredCount: number;
  /** 1-based position of the hand on screen; 0 when the filtered set is empty. */
  currentPosition: number;
  onJumpToPosition: (position: number) => void;
  disabled?: boolean;
  /** Drawn at the right end of the row - the page's Previous / Next (user, 2026-10-09). */
  trailing?: ReactNode;
}

// In the order a reviewer thinks about them: everything, the AI was right, the AI was wrong, the
// AI had nothing to say. "Declined" earns its own entry because it is most of the pool (2026-08-20).
const OUTCOME_OPTIONS: PredictionOutcomeFilter[] = ['all', 'matched', 'mismatched', 'declined'];

const ALL_SUBCATEGORIES = '__all__';

const SELECT_CLASS =
  'min-h-[32px] rounded-md border border-line-strong bg-card px-2 py-1 text-sm text-ink disabled:opacity-50';

export function PredictionFilterBar({
  categories,
  categoryId,
  onCategoryChange,
  subcategoryId,
  onSubcategoryChange,
  outcomeFilter,
  onOutcomeChange,
  filteredCount,
  currentPosition,
  onJumpToPosition,
  disabled = false,
  trailing,
}: PredictionFilterBarProps) {
  const [jumpValue, setJumpValue] = useState(String(currentPosition || ''));

  // Follow the page when it moves for any other reason (Next, a filter change, a restored
  // bookmark), so the box never shows a stale position the reviewer did not type.
  useEffect(() => {
    setJumpValue(String(currentPosition || ''));
  }, [currentPosition]);

  const commitJump = () => {
    const parsed = Number.parseInt(jumpValue, 10);
    if (Number.isNaN(parsed) || filteredCount === 0) {
      setJumpValue(String(currentPosition || ''));
      return;
    }
    // Clamp rather than reject: a reviewer typing 999 into a 705-hand set means "the end".
    const clamped = Math.min(Math.max(parsed, 1), filteredCount);
    setJumpValue(String(clamped));
    onJumpToPosition(clamped);
  };

  const category = categories.find(c => c.id === categoryId) ?? null;
  const subcategories = category?.subcategories ?? [];

  // The category is always set, so "filtered" means narrowed WITHIN it.
  const isFiltered = subcategoryId !== null || outcomeFilter !== 'all';

  return (
    <div
      className="flex flex-wrap items-end gap-x-4 gap-y-2"
      data-testid="prediction-filters"
    >
      <div className="flex flex-col gap-1">
        <label htmlFor="prediction-category" className="whitespace-nowrap text-[12px] font-semibold text-cream">
          Category
        </label>
        <select
          id="prediction-category"
          value={categoryId ?? ''}
          disabled={disabled || categories.length === 0}
          onChange={e => onCategoryChange(Number(e.target.value))}
          className={SELECT_CLASS}
        >
          {categories.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="prediction-subcategory" className="whitespace-nowrap text-[12px] font-semibold text-cream">
          Sub-category
        </label>
        <select
          id="prediction-subcategory"
          value={subcategoryId ?? ALL_SUBCATEGORIES}
          disabled={disabled || category === null}
          onChange={e =>
            onSubcategoryChange(e.target.value === ALL_SUBCATEGORIES ? null : Number(e.target.value))
          }
          className={SELECT_CLASS}
        >
          <option value={ALL_SUBCATEGORIES}>All sub-categories</option>
          {subcategories.map(s => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="prediction-outcome" className="whitespace-nowrap text-[12px] font-semibold text-cream">
          AI prediction
        </label>
        <select
          id="prediction-outcome"
          value={outcomeFilter}
          disabled={disabled}
          onChange={e => onOutcomeChange(e.target.value as PredictionOutcomeFilter)}
          className={SELECT_CLASS}
        >
          {OUTCOME_OPTIONS.map(option => (
            <option key={option} value={option}>
              {OUTCOME_FILTER_LABELS[option]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="prediction-jump" className="whitespace-nowrap text-[12px] font-semibold text-cream">
          Go to hand
        </label>
        <div className="flex items-center gap-2">
          <input
            id="prediction-jump"
            type="number"
            min={1}
            max={Math.max(filteredCount, 1)}
            value={jumpValue}
            disabled={disabled || filteredCount === 0}
            onChange={e => setJumpValue(e.target.value)}
            onBlur={commitJump}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitJump();
              }
            }}
            className="min-h-[32px] w-20 rounded-md border border-line-strong bg-card px-2 py-1 text-sm text-ink disabled:opacity-50"
          />
          <span className="whitespace-nowrap text-[13px] text-sand" data-testid="filter-count">
            of {filteredCount}
          </span>
        </div>
      </div>

      {isFiltered && (
        <button
          type="button"
          onClick={() => {
            // Back to the whole category: the category itself is never cleared, there being no
            // all-categories view to clear it to.
            onSubcategoryChange(null);
            onOutcomeChange('all');
          }}
          disabled={disabled}
          data-testid="clear-filters"
          className="min-h-[32px] rounded-md border border-brass bg-cream px-3 py-1 text-sm font-medium text-ink hover:bg-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:opacity-50"
        >
          Clear filters
        </button>
      )}

      {trailing && <div className="ml-auto">{trailing}</div>}
    </div>
  );
}
